import 'swiper/css';
import { Autoplay } from 'swiper/modules';
import { SwiperSlide } from 'swiper/react';

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Button from 'components/field/Button';
import Alert from 'components/global/Alert';
import Authorization from 'components/global/Authorization';
import Image from 'components/global/Image';
import SwiperFooter from 'components/global/SwiperFooter';
import Tabs from 'components/global/Tabs';
import Typography from 'components/global/Typography';

import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useMediaQuery from 'hooks/useMediaQuery';

import { ADMIN } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';
import PATHS from 'utils/paths';

import { faArrowUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons';

import AnnouncementSlideContent from '../AnnouncementSlideContent';
import { SwiperWrapper } from '../OnboardingV2/Onboarding.styled';
import SupportCards from '../SupportCards';
import HomeCards from './HomeCards';
import MissingInformationCard from './MissingInformation';
import QuickOverview from './QuickOverview';
import * as S from './RecurringHomePage.styled';
import ShipmentsNeedAction from './ShipmentsNeedAction';

const RecurringHomePage = () => {
	const user = useSelector((state) => state.user);
	const { t } = useTranslation();
	const isMobile = useMediaQuery(Breakpoints.tablet);

	const tabs = useMemo(
		() => [
			{
				label: 'overview',

				component: () => <Overview />
			},
			{
				label: 'tailoredForYou',
				path: '/home/tailoredForYouBoard',
				component: () => <TailoredForYou />
			}
		],
		[]
	);

	return (
		<S.ContentContainer>
			<S.PageHeader>
				<Typography variant="h4">
					{t('greetings')}, {user?.firstName}
				</Typography>
				<Typography variant="p3" noWrap={true} color="var(--content-3)">
					recurringHomePageCaption
				</Typography>
			</S.PageHeader>
			<HomeCards />
			{isMobile ? (
				<Tabs data={tabs} />
			) : (
				<S.DesktopView>
					<Overview />
					<TailoredForYou />
				</S.DesktopView>
			)}
		</S.ContentContainer>
	);
};

export default RecurringHomePage;

const Overview = () => {
	const user = useSelector((state) => state.user);
	const onboarding = useSelector((state) => state.instruction);
	const isEnterprise = user?.planName === 'EnterprisePackage';
	const navigate = useNavigate();
	const isMobile = useMediaQuery(Breakpoints.mobile);

	return (
		<S.LeftSide>
			{onboarding.shipmentCheck ? (
				<>
					<Authorization path="/home/otoPerks">
						<S.OtoPerksAnnouncement>
							<S.OtoPerksAnnouncementImage>
								<Image
									width="10rem"
									height="11rem"
									url={
										'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/cta/perks-icon.svg'
									}
									className="pt-3"
								/>
							</S.OtoPerksAnnouncementImage>
							<S.OtoPerksAnnouncementContent>
								<S.OtoPerksAnnouncementText>
									<Typography variant="p3" fontWeight="700" color="var(--content-3)" noWrap>
										perksAnnouncementDescriptionHeader
									</Typography>
									<Typography variant="p3" fontWeight="500" noWrap>
										perksAnnouncementDescription
									</Typography>
								</S.OtoPerksAnnouncementText>
								<S.OtoPerksAnnouncementButton>
									<Button
										label="viewPerks"
										variant="outline"
										icon={faArrowUpRightFromSquare}
										iconPos="right"
										target="_blank"
										href="https://oto.joinsecret.com/"
										component="a"
									/>
								</S.OtoPerksAnnouncementButton>
							</S.OtoPerksAnnouncementContent>
						</S.OtoPerksAnnouncement>
					</Authorization>
					<ShipmentsNeedAction />
					<MissingInformationCard />
					<QuickOverview />
					<SupportCards />
				</>
			) : (
				<>
					{!isMobile && <QuickOverview />}
					<MissingInformationCard />
					<SupportCards />
				</>
			)}

			{!isEnterprise && (
				<Alert
					showIcon
					iconUrl="https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/upgradeAccount.svg"
					header="upgradeAccountHeader"
					content="upgradeAccountDescription"
					ctaButtonLabel="learnMore"
					onCtaButtonClick={() => navigate(PATHS.OTO_MODULES)}
					style={{
						backgroundColor: 'var(--table-active-color)'
					}}
				/>
			)}
		</S.LeftSide>
	);
};

const TailoredForYou = () => {
	const {
		localization: { dir }
	} = useSelector((state) => state.layout);
	const [swiperRef, setSwiperRef] = useState(null);

	const [activeAnnouncements, setActiveAnnouncements] = useState([]);

	const axiosPrivate = useAxiosPrivate();

	useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await axiosPrivate.post(ADMIN.GET_ACTIVE_ANNOUNCEMENTS);
				const { data, status } = response;

				if (status === 200) {
					setActiveAnnouncements(data);
				} else {
					AppLogger.error();
				}
			} catch (error) {
				AppLogger.error(error);
			}
		};

		fetchData();
	}, []);

	return (
		<Authorization path="/home/tailoredForYouBoard">
			<S.RightSide>
				<S.Announcements padding={0}>
					<SwiperWrapper
						key={`recurring-homepage-${dir}`}
						dir={dir}
						modules={[Autoplay]}
						autoplay={{
							delay: 3000,
							disableOnInteraction: false
						}}
						onSwiper={setSwiperRef}>
						{activeAnnouncements.map((slide, index) => {
							return (
								<SwiperSlide virtualIndex={index} key={index} style={{ overflow: 'hidden' }}>
									<AnnouncementSlideContent slide={slide} />
								</SwiperSlide>
							);
						})}
						{activeAnnouncements.length > 0 && swiperRef && <SwiperFooter swiperRef={swiperRef} />}
					</SwiperWrapper>
				</S.Announcements>
			</S.RightSide>
		</Authorization>
	);
};
